/**
 * Analytics event catalog.
 *
 * Single source of truth for every trackable event in VIZ. Adding a new
 * event? Add it here, name it `snake_case_verb_past_tense`, and define its
 * payload type. The `useAnalytics` hook enforces that you can only fire
 * events listed in this file.
 *
 * Two destinations today:
 *   1. PostHog (external product analytics) — used for funnels, retention,
 *      cohorts. Initialized in main.tsx via `VITE_POSTHOG_KEY`.
 *   2. Supabase `analytics_events` table — existing in-database record for
 *      admin-visible reporting. Preserved to avoid breaking admin queries.
 */

// ============================================================================
// Event catalog
// ============================================================================

export type AnalyticsEventMap = {
  // --- Auth ---------------------------------------------------------------
  signup_started: { method: "email" | "google" | "apple" };
  signup_completed: { method: "email" | "google" | "apple" };
  signup_failed: { method: "email" | "google" | "apple"; error: string };
  login_completed: { method: "email" | "google" | "apple" };

  // --- Onboarding ---------------------------------------------------------
  onboarding_step_completed: { step: string; step_index: number };
  onboarding_skipped_optional: { step: string };
  onboarding_completed: { total_steps_completed: number };

  // --- Subscription -------------------------------------------------------
  subscribe_checkout_started: { plan: "essential" | "elite"; billing_cycle: "monthly" | "yearly" };
  subscribe_checkout_failed: { plan: string; error: string };
  subscribe_activated: { plan: "essential" | "elite" };

  // --- Creation flow (per phase entry) -----------------------------------
  creation_phase_entered: {
    visualization_type: "competition" | "practice" | "recovery";
    phase: "questionnaire" | "voice" | "outline-loading" | "outline-review" | "generating" | "pre-rating" | "player" | "post-rating";
  };
  voice_previewed: { voice_id: string; source: "onboarding" | "creation" | "profile" };
  voice_selected: { voice_id: string; source: "onboarding" | "creation" | "profile" };
  outline_change_requested: { visualization_id: string; change_length: number };
  visualization_started: { visualization_type: string }; // kept for compatibility with existing analytics_events
  generation_failed: {
    visualization_type: string;
    phase: "outline" | "script" | "audio";
    error_type: "timeout" | "rate_limit" | "ai_error" | "credit_limit" | "unknown";
    message?: string;
  };
  credit_limit_hit: { visualization_type: string };

  // --- Ratings ------------------------------------------------------------
  pre_rating_submitted: { visualization_type: string; pre_rating: number };
  session_completed: {
    visualization_type: string;
    lift: number;
    duration_seconds: number;
  }; // kept for compatibility with existing analytics_events

  // --- Library ------------------------------------------------------------
  library_session_played: { visualization_id: string };
  library_session_deleted: { visualization_id: string };
  library_session_favorited: { visualization_id: string; favorited: boolean };

  // --- Errors -------------------------------------------------------------
  app_error_boundary_caught: { message: string; component_stack?: string };
};

export type AnalyticsEventName = keyof AnalyticsEventMap;
export type AnalyticsEventPayload<K extends AnalyticsEventName> = AnalyticsEventMap[K];

// ============================================================================
// PostHog integration
// ============================================================================

import posthog from "posthog-js";

let posthogReady = false;

export function initAnalytics() {
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  if (!key) {
    if (import.meta.env.DEV) {
      console.info("[analytics] VITE_POSTHOG_KEY not set — PostHog disabled, Supabase-only");
    }
    return;
  }
  posthog.init(key, {
    api_host: (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com",
    // Autocapture off — we fire explicit named events only. Keeps the
    // schema meaningful and the bill predictable.
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: "localStorage+cookie",
    loaded: () => {
      posthogReady = true;
    },
  });
}

export function identifyUser(userId: string, traits?: Record<string, unknown>) {
  if (!posthogReady) return;
  posthog.identify(userId, traits);
}

export function resetAnalytics() {
  if (!posthogReady) return;
  posthog.reset();
}

export function capture<K extends AnalyticsEventName>(
  event: K,
  payload: AnalyticsEventPayload<K>,
) {
  if (!posthogReady) return;
  posthog.capture(event, payload as Record<string, unknown>);
}
